import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import lodashGet from "lodash/get";

import { makeStyles } from "@material-ui/styles";
import Card from "@material-ui/core/Card";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";

// import CircularProgress from "@material-ui/core/CircularProgress";
import ActionRun from "@material-ui/icons/PlayCircleFilled";
import ActionPrint from "@material-ui/icons/Print";
import ImageEye from "@material-ui/icons/RemoveRedEye";
import DeleteIcon from "@material-ui/icons/Delete";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import ExcelIcon from "mdi-material-ui/FileExcel";

import Collapse from "@material-ui/core/Collapse";
import Fade from "@material-ui/core/Fade";
import CircularProgress from "@material-ui/core/CircularProgress";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import { WithReduxForm, PageHeader, Button, NumberInput, SelectArrayInput, showNotification, translate, minValue, maxValue } from "jazasoft";
import { SimpleForm } from "jazasoft/lib/mui/form/SimpleForm";

import Tabs from "../../../components/tab/Tabs";
import SubTab from "../../../components/tab/SubTab";
import MuiTable from "../../../components/MuiTable";
import Table from "../../../components/Table";
import CardHeader from "../../../components/CardHeader";

import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSetting, useTenant, useUnit } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { Entity, OState, SpreadingType } from "../../../utils/types";
import { listFromCsv, getDistinctValues, isEmpty, inputOptions, isTrue, isFalse, getDistinctObjectValues, toFixedValue } from "../../../utils/helpers";
import { cbSortFlow, getCutOrderFlowInfoList, getEffectiveExtraPercent, parseMarkerName } from "../../../helpers/orderHelper";
import { BundleSheetType, LayState, OrderFlowType } from "../../../constants";
import hasPrivilege from "../../../utils/hasPrivilege";

import { reduceFlowInfo } from "../../cutplan/cutPlanHelper";
import { ResourceId, Scope } from "../../../utils/privilege";
import BundlingQtyDrawer from "./BundlingQtyDrawer";
import { colorLightGreen, colorSecondary, colorTomatoRed } from "../../../utils/colors";

import BundlingReport from "./BundlingReport";
import { printBundlePlan, printBundleTag, printSerialPlan, printPanelReplacementPlan, getSortedBundleList, printPieceTag } from "./bundleHelper";
import { truncateBundlePlanFilename } from "./bundleFilename";
import LayDialog from "./LayDialog";
import SplitButton from "../../../components/SplitButton";

const choicesBundleBreak = ["Shade", "Roll"].map(e => ({ id: e, name: e }));

// const bundleOrderFields = [
//   "productName",
//   "style",
//   "styleColor",
//   "fpo",
//   "unitId",
//   "effPartName",
//   "destination",
//   "deliveryDate",
//   "delMode",
//   "customerFlowRef",
//   "flowRef"
// ];
// const getBundleKey = row => bundleOrderFields.map(field => row[field] || "").join("#@#");

const delFields = ["externalFpo", "destination", "deliveryDate", "delMode", "customerFlowRef", "flowRef"];

const getDelKey = row => delFields.map(field => row[field] || "").join("#@#");

const StatusField = ({ record, column }) => {
  const state = record[column.dataKey];
  return (
    <div
      style={{
        backgroundColor:
          state === OState.BUNDLING_IN_COMPLETED.state
            ? colorLightGreen.dark
            : state === OState.BUNDLING_PENDING.state
              ? colorTomatoRed.dark
              : colorSecondary.dark,
        color: "white",
        padding: "4px 8px",
        borderRadius: 4,
        width: "100%"
      }}>
      {state}
    </div>
  );
};

const QtyField = ({ record, column }) => {
  const value = record[column.dataKey];
  return (
    <div>
      {value} {record.uomQty || "Pc"}
    </div>
  );
};

const renderLayStatus = value => {
  return (
    <div
      style={{
        backgroundColor:
          value === "Bundling Completed" ? colorLightGreen.dark : value === "Feedback Pending" ? colorTomatoRed.dark : colorSecondary.dark,
        color: "white",
        padding: "4px 8px",
        borderRadius: 4,
        width: "100%"
      }}>
      {value}
    </div>
  );
};

const Action = ({ idx, record }) => (
  <div style={{ width: 50 }}>
    <span className="handle">
      <DragHandleIcon style={{ cursor: "grab", marginLeft: 4 }} />
    </span>
  </div>
);

const useStyles = makeStyles({
  content: {
    margin: "1.5em",
    marginTop: 0
  },
  mrTop: {
    marginTop: "1.5em"
  },
  table: {
    whiteSpace: "pre"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  },
  margin: {
    margin: "1.5em"
  },
  form: {
    padding: "1.5em",
    paddingTop: 0
  },
  settings: {
    margin: "1em 1em 0 1em"
  },
  footer: {
    padding: "1.5em",
    display: "flex",
    justifyContent: "center"
  },
  footerSetting: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0.5rem"
  }
});

const BundlingView = props => {
  const classes = useStyles();
  const { id, roles, hasAccess, translate } = props;

  const [processingLayAction, setProcessingLayAction] = React.useState(null);
  const [version, setVersion] = React.useState(0);
  const [selectedKey, setSelectedKey] = React.useState(null);
  const [selectedKeys, setSelectedKeys] = React.useState([]);
  const [tabIdx, setTabIdx] = React.useState(0);
  const [drawerState, setDrawerState] = React.useState({ open: false, bundleOrder: null, flowInfoList: [] });
  const [dialogState, setDialogState] = React.useState({ open: false, lay: {} });
  const [bundleOrderRows, setBundleOrderRows] = React.useState([]);
  const [editingSequence, setEditingSequence] = React.useState(false);
  const [editingSetting, setEditingSetting] = React.useState(false);

  const tenant = useTenant();
  const dispatch = useDispatch();
  const [mutate, { loading: busy }] = useMutation();
  const [download] = useMutation({ rawResponse: true });

  const enablePieceTagVal = useSetting(SettingKeys.ENABLE_BUNDLE_PIECE_TAG);
  const resetDigitsVal = useSetting(SettingKeys.NUMBERING_SERIAL_RESET_DIGITS);
  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ORDERS);
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FLOW_INFO);
  const rollFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ROLLS);
  const layFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.LAYS);
  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);
  const bundleTagSettingVal = useSetting(SettingKeys.BUNDLE_TAG_SETTING);
  const pieceTagSettingVal = useSetting(SettingKeys.PIECE_TAG_SETTING);
  const bundleExpotFieldVal = useSetting(SettingKeys.BUNDLE_EXPORT_FIELDS);
  const bundleTagSetting = isEmpty(bundleTagSettingVal) ? {} : JSON.parse(bundleTagSettingVal);
  const pieceTagSetting = isEmpty(pieceTagSettingVal) ? {} : JSON.parse(pieceTagSettingVal);
  const bundleExportFields = listFromCsv(bundleExpotFieldVal);
  const enablePieceTag = isTrue(enablePieceTagVal);
  const resetDigits = isEmpty(resetDigitsVal) ? 100 : Number(resetDigitsVal);

  const messages = useSelector(state => state?.i18n?.messages || {});
  const { allUnitList } = useUnit();

  const { data: order = {} } = useQuery({ url: `orders/${id}`, params: { page: "bundleOrder" }, version });

  const cutOrderRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList) || isEmpty(order?.orderFlowList)) return [];

    const partSerial = !isEmpty(productParts) ? listFromCsv(productParts).reduce((acc, part, idx) => ({ ...acc, [part]: idx }), {}) : {};

    const cutOrderList = order.orderFlowList.filter(e => e.type === OrderFlowType.CUTPLAN).sort((a, b) => cbSortFlow(a, b, partSerial));
    const summaryKeys = getDistinctValues(cutOrderList.map(row => row?.bundleSummaryKey));

    const rowList = summaryKeys.map(summaryKey => {
      const coList = cutOrderList.filter(row => row?.bundleSummaryKey === summaryKey);
      const flowInfoList = coList.flatMap(cutOrder => getCutOrderFlowInfoList(order, cutOrder));
      const state = coList.sort((a, b) => a.stateValue - b.stateValue)[0]?.state;

      return {
        ...reduceFlowInfo(flowInfoList, order),
        key: summaryKey,
        state,
        effPartName: coList[0]?.part,
        cutOrderList: coList,
        flowInfoList,
        poRef: order?.poRef,
        customerName: order?.customer?.name,
        seasonName: order?.season?.name
      };
    });

    return rowList;
  }, [order, productParts]);

  const cutOrderColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "unitId", title: "Unit", render: value => allUnitList?.find(e => e.unitId === value)?.name || value, highlight: true },
      { dataKey: "fpo", title: "FPO", highlight: true },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "effPartName", title: "Part", highlight: true },
      { dataKey: "orderQty", title: "Order Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      { dataKey: "fpoQty", title: "FPO Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      { dataKey: "destination", title: "Destination", filterOptions: { filter: true }, highlight: true },
      { dataKey: "deliveryDate", title: "Delivery Date", filterOptions: { filter: true }, highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "state", title: "Status", filterOptions: { filter: true }, length: 100 }
    ];

    if (cutOrderRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (cutOrderRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (cutOrderRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (cutOrderRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (cutOrderRows.every(e => isEmpty(e.fpo))) {
      cols = cols.filter(c => c.dataKey !== "fpo");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "fpoQty");
    }
    return cols;
  }, [cutOrderRows, allUnitList]);

  useDeepEffect(() => {
    if (!isEmpty(cutOrderRows)) {
      setSelectedKey(cutOrderRows[0].key);
    }
  }, [cutOrderRows.length]);

  const onRowClick = row => {
    if (isEmpty(row.parentKey)) {
      setSelectedKey(row.key);
    }
  };

  const selectedRow = cutOrderRows.find(e => e.key === selectedKey);
  const cutOrderList = selectedRow?.cutOrderList || [];
  const cutOrder = cutOrderList[0];
  const orderConfig = cutOrderList.find(co => !isEmpty(co.orderConfig?.bundleParts))?.orderConfig || cutOrder?.orderConfig;

  const flowInfoList = selectedRow?.flowInfoList || [];
  const productList = getDistinctObjectValues(flowInfoList.map(fi => fi.product).filter(e => !isEmpty(e)));
  const product = productList.length === 1 ? productList[0] : order?.flowInfoList?.find(fi => fi.product?.id === cutOrder?.parentProductId)?.product;

  const productIdList = getDistinctValues(cutOrderList.flatMap(cutOrder => cutOrder?.productIdList || []));

  const { data: layList = [] } = useQuery(
    !isEmpty(cutOrderList) && { url: `lays`, params: { search: `orderFlow.id=in=(${cutOrderList.map(e => e.id).join(",")})` }, version }
  );
  const { data: layRowList = [] } = useQuery(
    !isEmpty(cutOrderList) && {
      url: "lay-rows",
      params: { search: `completed=='TRUE';lay.orderFlow.id=in=(${cutOrderList.map(e => e.id).join(",")})` }
    }
  );

  const { data: rollInfoList = [] } = useQuery(
    !isEmpty(cutOrderList) && {
      url: "rolls",
      params: { search: `parentId=='NULL';cutOrderFlow.id=in=(${cutOrderList.map(e => e.id).join(",")})`, size: 100000 }
    }
  );

  const bundleOrderList =
    order?.orderFlowList?.filter(e => e.type === OrderFlowType.BUNDLING).filter(e => e.bundleOrderParentKey === selectedKey) || [];

  const bundleOrderIds = bundleOrderList?.map(bo => bo.id) || [];
  const { data: bundleList = [] } = useQuery(
    !isEmpty(bundleOrderIds) && { url: `bundles`, params: { search: `bundleOrder.id=in=(${bundleOrderIds.join(",")})`, version } }
  );

  useDeepEffect(() => {
    if (isEmpty(bundleOrderList)) {
      setTabIdx(1);
    } else {
      setTabIdx(0);
    }
  }, [bundleOrderList]);

  const onSelect = selectedKeys => {
    setSelectedKeys(selectedKeys);
  };

  useDeepEffect(() => {
    if (isEmpty(selectedRow) || isEmpty(selectedRow.flowInfoList)) return;

    const flowInfoList = selectedRow.flowInfoList || [];
    let rows = [];
    if (tabIdx === 0) {
      rows = bundleOrderList
        .sort((a, b) => a.serialNo - b.serialNo)
        .map(bundleOrder => {
          const currFlowInfoList = flowInfoList
            .filter(fi => bundleOrder.productIdList.includes(fi.productId))
            .filter(fi => bundleOrder.styleColorList.includes(fi.styleColor))
            .filter(fi => isEmpty(bundleOrder.destinations) || listFromCsv(bundleOrder.destinations).includes(fi.destination))
            .filter(fi => isEmpty(bundleOrder.deliveries) || listFromCsv(bundleOrder.deliveries).includes(fi.deliveryDate))
            .filter(fi => isEmpty(bundleOrder.delModes) || listFromCsv(bundleOrder.delModes).includes(fi.delMode))
            .filter(fi => isEmpty(bundleOrder.flowRefs) || listFromCsv(bundleOrder.flowRefs).includes(fi.flowRef))
            .filter(fi => isEmpty(bundleOrder.customerFlowRefs) || listFromCsv(bundleOrder.customerFlowRefs).includes(fi.customerFlowRef));

          const uomQty = bundleOrder.productIdList.length > 1 ? "Set" : "Pc";
          const productId = bundleOrder.productIdList[0];
          const productName = getDistinctValues(currFlowInfoList.map(fi => fi.product?.name)).join(", ");

          const sizeBreakupList = currFlowInfoList
            .filter(fi => fi.productId === productId)
            .flatMap(fi => fi.sizeBreakupList?.filter(sb => sb.type === "ORDER").map(sb => ({ ...sb, qty: sb.qty / (fi.ratio || 1) })) || []);
          const fpoSizeBreakupList = currFlowInfoList
            .filter(fi => fi.productId === productId)
            .flatMap(fi => fi.fpoSizeBreakupList.map(sb => ({ ...sb, qty: sb.qty / (fi.ratio || 1) })) || []);

          const orderQty = getDistinctObjectValues(sizeBreakupList)?.reduce((acc, sb) => acc + sb.qty, 0);
          const fpoQty = getDistinctObjectValues(fpoSizeBreakupList)?.reduce((acc, sb) => acc + sb.qty, 0);

          const bundlingQty = bundleOrder.boSizeBreakupList?.reduce((acc, sb) => acc + sb.qty, 0) || 0;
          const bundleQty = bundleList.filter(b => b.bundleOrderId === bundleOrder.id).reduce((acc, b) => acc + b.qty, 0);

          const extra = getEffectiveExtraPercent(currFlowInfoList);

          let actionElements = [
            <ImageEye key="view" style={{ marginRight: 8, cursor: "pointer" }} onClick={() => setDrawerState({ open: true, bundleOrder })} />
          ];

          return {
            ...bundleOrder,
            externalFpo: bundleOrder.fpo === cutOrder?.fpo ? undefined : bundleOrder.fpo,
            productName,
            style: bundleOrder.styleList?.join(", "),
            color: bundleOrder.colorList?.join(", "),
            destination: bundleOrder.destinations,
            deliveryDate: bundleOrder.deliveries,
            delMode: bundleOrder.delModes,
            flowRef: bundleOrder.flowRefs,
            customerFlowRef: bundleOrder.customerFlowRefs,
            orderQty,
            extra: `${extra || 0}%`,
            extraPercent: extra,
            fpoQty,
            bundlingQty,
            bundleQty,
            uomQty,
            sizeBreakupList,
            fpoSizeBreakupList,
            dragAction: <Action record={bundleOrder} />,
            action: actionElements
          };
        });
    } else {
      const completedFlowInfoList = bundleOrderList.flatMap(bundleOrder =>
        flowInfoList
          .filter(fi => fi.externalFpo === bundleOrder.fpo)
          .filter(fi => bundleOrder.productIdList.includes(fi.productId))
          .filter(fi => bundleOrder.styleList.includes(fi.style))
          .filter(fi => bundleOrder.colorList.includes(fi.color))
          .filter(fi => isEmpty(bundleOrder.destinations) || listFromCsv(bundleOrder.destinations).includes(fi.destination))
          .filter(fi => isEmpty(bundleOrder.deliveries) || listFromCsv(bundleOrder.deliveries).includes(fi.deliveryDate))
          .filter(fi => isEmpty(bundleOrder.delModes) || listFromCsv(bundleOrder.delModes).includes(fi.delMode))
          .filter(fi => isEmpty(bundleOrder.flowRefs) || listFromCsv(bundleOrder.flowRefs).includes(fi.flowRef))
          .filter(fi => isEmpty(bundleOrder.customerFlowRefs) || listFromCsv(bundleOrder.customerFlowRefs).includes(fi.customerFlowRef))
      );

      const completedFlowInfoIds = completedFlowInfoList.map(fi => fi.id);

      const boSummaryList = getDistinctObjectValues(
        cutOrderList.map(c => ({
          id: `${c.productIdList.sort().join("_")}__${c.styleColorList.join("_")}`,
          productIdList: c.productIdList,
          styleColorList: c.styleColorList
        }))
      );

      rows = boSummaryList
        .flatMap(({ id, productIdList, styleColorList }) => {
          const fiList = flowInfoList.filter(fi => productIdList.includes(fi.product?.id) && styleColorList.includes(fi.styleColor));
          if (isEmpty(fiList)) return [];
          if (fiList.every(fi => completedFlowInfoIds.includes(fi.id))) return [];

          const productName = getDistinctValues(fiList.map(fi => fi.product?.name)).join(", ");
          const style = getDistinctValues(fiList.map(fi => fi.style)).join(", ");
          const color = getDistinctValues(fiList.map(fi => fi.color)).join(", ");
          const uomQty = productIdList.length > 1 ? "Set" : "Pc";
          const productId = productIdList[0];

          const delKeys = getDistinctValues(fiList.map(fi => getDelKey(fi)));
          return delKeys
            .map(delKey => {
              const delFiList = fiList.filter(fi => getDelKey(fi) === delKey);

              return {
                ...delFiList[0],
                key: `${id}__${delKey}`,
                productName,
                style,
                color,
                flowInfoList: delFiList,
                orderQty: getDistinctObjectValues(delFiList)
                  ?.filter(fi => fi.product?.id === productId)
                  .reduce((acc, fi) => acc + (fi.orderQty || 0) / (fi.ratio || 1), 0),
                fpoQty: getDistinctObjectValues(delFiList)
                  ?.filter(fi => fi.product?.id === productId)
                  .reduce((acc, fi) => acc + (fi.fpoQty || 0) / (fi.ratio || 1), 0),
                uomQty
              };
            })
            .filter(row => !row.flowInfoList.every(fi => completedFlowInfoIds.includes(fi.id)));
        })
        .map(({ parentId, ...row }) => row);
    }
    setBundleOrderRows(rows);
  }, [
    tabIdx,
    selectedRow,
    bundleList,
    cutOrder,
    cutOrderList,
    bundleOrderList?.map(e => `${e.id}-${e.state}-${JSON.stringify(e.boSizeBreakupList)}`)
  ]);

  const bundleOrderColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "dragAction", title: "Action" },
      { dataKey: "bundleOrderNo", title: "Bundle Order No", filterOptions: { filter: true }, highlight: true },
      { dataKey: "externalFpo", title: "Ext. FPO", filterOptions: { filter: true }, highlight: true },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "destination", title: "Destination", filterOptions: { filter: true }, highlight: true },
      { dataKey: "deliveryDate", title: "Delivery Date", filterOptions: { filter: true }, highlight: true },
      { dataKey: "delMode", title: "Delivery Mode", filterOptions: { filter: true }, highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "extra", title: "Extra Ship (%)", align: "right", render: value => `${value || 0}%` },
      { dataKey: "orderQty", title: "Order Qty", align: "right", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}`, element: <QtyField /> },
      { dataKey: "fpoQty", title: "FPO Qty", align: "right", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}`, element: <QtyField /> },
      {
        dataKey: "bundlingQty",
        title: "Bundle \nRequired Qty",
        render: (value, _, row) => `${value} ${row.uomQty || "Pc"}`,
        element: <QtyField />,
        align: "right"
      },
      {
        dataKey: "bundleQty",
        title: "Bundle \nCompleted Qty",
        render: (value, _, row) => `${value} ${row.uomQty || "Pc"}`,
        element: <QtyField />,
        align: "right"
      },
      { dataKey: "state", title: "Status", align: "center", element: <StatusField /> },
      { dataKey: "action", title: "Action", align: "center" }
    ];

    if (bundleOrderRows.every(e => isEmpty(e.externalFpo))) {
      cols = cols.filter(c => c.dataKey !== "externalFpo");
    }
    if (bundleOrderRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (bundleOrderRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (bundleOrderRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (bundleOrderRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (bundleOrderRows.every(e => isEmpty(e.delMode))) {
      cols = cols.filter(c => c.dataKey !== "delMode");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "fpoQty");
    }

    if (tabIdx === 1) {
      cols = cols.filter(c => !["bundleOrderNo", "bundlingQty", "bundleQty", "state", "dragAction", "action"].includes(c.dataKey));
    }

    if (tabIdx === 0) {
      if (editingSequence) {
        cols = cols.filter(c => c.dataKey !== "action");
      } else {
        cols = cols.filter(c => c.dataKey !== "dragAction");
      }
    }

    return cols.map(({ render, element, ...col }) => ({
      ...col,
      render: tabIdx === 1 ? render : undefined,
      element: tabIdx === 0 ? element : undefined
    }));
  }, [tabIdx, bundleOrderRows?.map(e => e.id), selectedRow, allUnitList, editingSequence]);

  const onBundleOrderCreateClick = () => {
    const fiList = bundleOrderRows.filter(e => selectedKeys.includes(e.key)).flatMap(e => e.flowInfoList || []);
    const flowInfoList = getDistinctObjectValues(fiList);

    const extFpoList = getDistinctValues(flowInfoList.filter(e => !isEmpty(e.externalFpo)).map(e => e.externalFpo));
    if (extFpoList.length > 1) {
      dispatch(showNotification("External FPO cannot be combined together.", { type: "danger" }));
      return;
    }
    const styleColorList = cutOrder?.styleColorList || [];
    const fiStyleColorList = flowInfoList.map(e => e.styleColor);
    if (styleColorList.length > 1 && styleColorList.some(sc => !fiStyleColorList.includes(sc))) {
      dispatch(
        showNotification(
          "Bundle Order segregation at Style or Color is not allowed. This segregation is available by default via Bundling logic. Hint: Select all Style/Color together",
          {
            type: "danger"
          }
        )
      );
      return;
    }

    setDrawerState({ open: true, flowInfoList });
  };

  const onSequenceUpdate = () => {
    const bundleOrderList = bundleOrderRows.map(({ id, bundleOrderNo }, idx) => ({ id, bundleOrderNo, serialNo: idx + 1 }));
    const options = {
      url: `bundle-orders`,
      method: "put",
      params: { orderId: order?.id, bundleOrderParentKey: selectedKey },
      data: bundleOrderList
    };
    mutate(options, {
      onSuccess: () => {
        setVersion(v => v + 1);
        dispatch(showNotification(`Bundle Order sequence updated successfully `));
        setEditingSequence(false);
      }
    });
  };

  const onSettingUpdate = setting => {
    const { bundleSize, bundleTolerance, bundleBreakAt, ...bundleParts } = setting;

    const options = {
      url: `bundle-orders/setting`,
      method: "put",
      params: { orderId: order?.id, bundleOrderParentKey: selectedKey },
      data: {
        bundleSize,
        bundleTolerance,
        bundleBreakAt: bundleBreakAt?.join(","),
        bundleParts: JSON.stringify(Object.keys(bundleParts).map(key => ({ productId: key.split("-")[0], parts: bundleParts[key]?.join(",") })))
      }
    };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Bundle Setting updated successfully."));
        setVersion(v => v + 1);
        setEditingSetting(false);
      }
    });
  };

  const layColumns = useDeepMemo(() => {
    if (isEmpty(cutOrderList)) return [];

    const lotValues = getDistinctValues(cutOrderList.map(e => e.lotValue));
    const flowInfoList = selectedRow.flowInfoList || [];
    const sizeBreakupList = flowInfoList.flatMap(flowInfo => flowInfo.sizeBreakupList || []);
    const haveSizeGroup = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;

    let columns = [
      { dataKey: "lotValue", title: "Lot", align: "left" },
      { dataKey: "layNo", title: "Lay No", align: "center" },
      { dataKey: "extLayNo", title: "Ext Lay No", align: "center" },
      { dataKey: "cutNo", title: "Cut No", align: "center" },
      { dataKey: "sizeGroup", title: "Size Group", align: "center" },
      { dataKey: "markerNo", title: "Marker No", align: "center" },
      { dataKey: "productName", title: "Product", render: value => value || "-" },
      { dataKey: "markerName", title: "Marker", length: 100 },
      { dataKey: "way", title: "Way", align: "center" },
      { dataKey: "actualPlies", title: "Actual Plies", align: "center", render: value => toFixedValue(value, 0) },
      { dataKey: "cutQty", title: "Cut Qty", align: "center", render: value => toFixedValue(value, 0) },
      { dataKey: "bundleGeneratedTime", title: "Bundle Generated At", align: "center" },
      {
        dataKey: "status",
        title: "Lay Status",
        headerCellProps: { style: { width: 180 } },
        rowCellProps: { style: { width: 180 } },
        align: "center",
        render: renderLayStatus
      }
    ];

    if (!haveSizeGroup) {
      columns = columns.filter(c => c.dataKey !== "sizeGroup");
    }

    if (lotValues.length <= 1) {
      columns = columns.filter(c => c.dataKey !== "lotValue");
    }
    if (productIdList.length <= 1) {
      columns = columns.filter(c => c.dataKey !== "productName");
    }

    return columns.filter(c => [...layFields, "productName"].includes(c.dataKey));
  }, [selectedRow, cutOrderList, layFields, productIdList]);

  const toNumberIfAllCutNoNumeric = list => {
    const cutNoValues = list.map(e => e.cutNo).filter(e => !isEmpty(e));

    const allCutNoNumeric = !isEmpty(cutNoValues) && cutNoValues.every(value => !Number.isNaN(Number(value)));

    return list.map(row => ({
      ...row,
      cutNo: isEmpty(row.cutNo) ? "-" : allCutNoNumeric ? Number(row.cutNo) : row.cutNo
    }));
  };

  const layRows = useDeepMemo(() => {
    if (isEmpty(cutOrderList) || isEmpty(layList)) return [];

    const cutOrderIds = cutOrderList.map(e => e.id);
    const rows = layList
      .filter(lay => cutOrderIds.includes(lay.orderFlowId))
      .map(lay => ({ ...lay, cutOrder: cutOrderList.find(e => e.id === lay.orderFlowId) }))
      .map(lay => ({ ...lay, marker: lay.cutOrder?.markerList?.find(m => m.id === lay.markerId) }))
      .sort((a, b) => (a.cutOrder?.plannedAt !== b.cutOrder?.plannedAt ? a.cutOrder?.plannedAt - b.cutOrder?.plannedAt : a.layNo - b.layNo))
      .map(lay => {
        const actualPlies = layRowList
          .filter(e => e.markerId === lay.markerId)
          .filter(e => isFalse(e.skipped) && e.actualLayNo === lay.layNo)
          .reduce((acc, row) => acc + row.actualPly || 0, 0);

        const status = isTrue(lay.bundleGenerated) ? "Bundling Completed" : lay.state;

        const { sizeGroup, name } = parseMarkerName(lay.marker.name);
        const productName = productList?.find(e => e.id === lay.marker?.productId)?.name;

        const cutPlies = lay?.marker?.spreadingType !== SpreadingType.FACE_ONE_WAY ? actualPlies / 2 : actualPlies;

        return {
          ...lay,
          sizeGroup,
          markerNo: lay.marker?.markerNo,
          markerName: name,
          productName,
          lotValue: lay.cutOrder?.lotValue,
          way: lay.marker?.noOfWays || 0,
          actualPlies,
          cutQty: cutPlies * (lay.marker?.noOfWays || 0),
          bundleGeneratedTime: isEmpty(lay.bundleGeneratedAt) ? "-" : format(lay.bundleGeneratedAt, "dd MMM yy hh:mm:ss a"),
          status
        };
      });

    return toNumberIfAllCutNoNumeric(rows);
  }, [cutOrderList, layRowList, layList]);

  const onInlineActionClick = (event, action, row, onActionComplete) => {
    if (processingLayAction) {
      dispatch(showNotification("Please wait, previous action is still in progress.", { type: "warning" }));
      return;
    }

    if (action === "view") {
      if (!isEmpty(row)) {
        setDialogState({ open: true, lay: row });
      }
      return;
    }
    const clearProcessing = () => {
      setTimeout(() => {
        setProcessingLayAction(null);
      }, 250);
    };

    const fallbackTimer = setTimeout(() => {
      clearProcessing();
    }, 10000);

    const finishProcessing = () => {
      clearTimeout(fallbackTimer);
      clearProcessing();
    };

    const message =
      action === "delete"
        ? `Deleting bundles for Lay No: ${row.layNo}. Please wait...`
        : `Generating bundles for Lay No: ${row.layNo}. Please wait...`;

    setProcessingLayAction({
      layId: row.id,
      action,
      message
    });

    const commonCallbacks = {
      onSuccess: () => {
        finishProcessing();
        setVersion(v => v + 1);

        dispatch(
          showNotification(
            action === "delete" ? `Bundle deleted successfully for Lay No: ${row.layNo}` : `Bundle generated successfully for Lay No: ${row.layNo}`
          )
        );

        onActionComplete && onActionComplete(row);
      },

      onError: () => {
        finishProcessing();
        dispatch(
          showNotification(
            action === "delete" ? `Bundle deletion failed for Lay No: ${row.layNo}` : `Bundle generation failed for Lay No: ${row.layNo}`,
            { type: "danger" }
          )
        );
      },

      onFailure: () => {
        finishProcessing();
      }
    };

    if (action === "generate") {
      mutate(
        {
          url: `bundles`,
          method: "post",
          params: { orderId: order?.id, layId: row.id }
        },
        commonCallbacks
      );
    } else if (action === "delete") {
      mutate(
        {
          url: `bundles`,
          method: "delete",
          params: { orderId: order?.id, layId: row.id }
        },
        commonCallbacks
      );
    }
  };
  const layInlineActions = lay => {
    let actions = [];

    const isAnyProcessing = !isEmpty(processingLayAction);
    const isCurrentLayProcessing = processingLayAction?.layId === lay.id;

    if (lay.state === LayState.COMPLETED && isFalse(lay.bundleGenerated)) {
      actions.push({
        name: "view",
        tooltip: "View LaySlip",
        icon: <ImageEye />,
        disabled: isAnyProcessing
      });

      actions.push({
        name: "generate",
        tooltip: isCurrentLayProcessing ? "Generating bundle, please wait..." : "Generate Bundle",
        icon: <ActionRun />,
        disabled: isAnyProcessing
      });
    }

    if (isTrue(lay.bundleGenerated)) {
      actions.push({
        name: "view",
        tooltip: "View LaySlip",
        icon: <ImageEye />,
        disabled: isAnyProcessing
      });

      actions.push({
        name: "delete",
        tooltip: isCurrentLayProcessing ? "Deleting bundles, please wait..." : "Delete Bundles",
        icon: <DeleteIcon />,
        disabled: isAnyProcessing,
        options: {
          style: {
            color: isCurrentLayProcessing ? "gray" : "red",
            cursor: isCurrentLayProcessing ? "not-allowed" : "pointer",
            opacity: isCurrentLayProcessing ? 0.5 : 1
          }
        }
      });
    }

    return actions;
  };
  const laySelectActions = useDeepMemo(() => {
    let actions = [
      {
        name: "bundle-plan",
        tooltip: "Bundle Plan",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 135 } }
      },
      {
        name: "bundle-plan-excel",
        tooltip: "Bundle Sheet",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 120 } }
      },
      {
        name: "bundle-tag",
        tooltip: "Bundle Tag",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 120 } }
      },
      {
        name: "piece-tag",
        tooltip: "Piece Tag",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 120 } }
      },
      {
        name: "serial-plan",
        tooltip: "Serial Plan",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 120 } }
      },
      {
        name: "panel-replacement-plan",
        tooltip: "Panel Replacement Plan",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 200 } }
      }
    ];

    if (!enablePieceTag) {
      actions = actions.filter(e => e.name !== "piece-tag");
    }

    return actions;
  }, [enablePieceTag]);

  const onLaySelectActionClick = (event, action, selectedItems, onActionComplete) => {
    const bundleNotGeneratedLays = selectedItems.filter(row => !isTrue(row.bundleGenerated));
    if (!isEmpty(bundleNotGeneratedLays)) {
      dispatch(
        showNotification(
          `Bundles are not generated for some Lays. Hint: Unselect the Lays (${bundleNotGeneratedLays
            ?.map(e => `Lay ${e.layNo}`)
            ?.join(",")}) and Try again.`,
          { type: "danger" }
        )
      );
      return;
    }

    if (action === "bundle-plan") {
      const selectedIds = selectedItems?.map(e => e.id);
      const bundles = bundleList.filter(e => selectedIds.includes(e.layId));
      const sortedBundleList = getSortedBundleList(bundles, "Bundle", { cutOrderList, bundleOrderList, layList, order });
      printBundlePlan({
        order,
        cutOrderList,
        bundleOrderList,
        layList: selectedItems,
        bundleList: sortedBundleList,
        tenant,
        flowInfoFields,
        orderFields,
        bundleExportFields,
        i18nMessages: messages,
        resetDigits,
        unit: allUnitList.find(e => e.unitId === selectedRow?.unitId) || {}
      });
    } else if (action === "bundle-plan-excel") {
      const layIds = selectedItems.map(e => e.id);
      onBundlePlanDownload(layIds);
    } else if (action === "bundle-tag") {
      const selectedIds = selectedItems?.map(e => e.id);
      const bundles = bundleList.filter(e => selectedIds.includes(e.layId));
      const sortedBundleList = getSortedBundleList(bundles, "Bundle", { cutOrderList, bundleOrderList, layList, order });
      printBundleTag({
        order,
        cutOrderList,
        bundleOrderList,
        layList: selectedItems,
        bundleList: sortedBundleList,
        bundleTagSetting,
        resetDigits
      });
    } else if (action === "piece-tag") {
      const selectedIds = selectedItems?.map(e => e.id);
      const bundles = bundleList.filter(e => selectedIds.includes(e.layId));
      const sortedBundleList = getSortedBundleList(bundles, "Bundle", { cutOrderList, bundleOrderList, layList, order });
      printPieceTag(sortedBundleList, pieceTagSetting, resetDigits);
    } else if (action === "serial-plan") {
      const selectedIds = selectedItems?.map(e => e.id);
      const bundles = bundleList.filter(e => selectedIds.includes(e.layId));
      const sortedBundleList = getSortedBundleList(bundles, "Serial", { cutOrderList, bundleOrderList, layList, order });
      printSerialPlan({
        order,
        cutOrderList,
        bundleOrderList,
        layList: selectedItems,
        bundleList: sortedBundleList,
        tenant,
        flowInfoFields,
        orderFields,
        i18nMessages: messages,
        resetDigits,
        unit: allUnitList.find(e => e.unitId === selectedRow?.unitId) || {}
      });
    } else if (action === "panel-replacement-plan") {
      const selectedIds = selectedItems?.map(e => e.id);
      const bundles = bundleList.filter(e => selectedIds.includes(e.layId));
      const sortedBundleList = getSortedBundleList(bundles, "Serial", { cutOrderList, bundleOrderList, layList, order });
      printPanelReplacementPlan({
        order,
        cutOrderList,
        bundleOrderList,
        layList: selectedItems,
        bundleList: sortedBundleList,
        rollInfoList,
        tenant,
        flowInfoFields,
        orderFields,
        i18nMessages: messages,
        resetDigits,
        unit: allUnitList.find(e => e.unitId === selectedRow?.unitId) || {}
      });
    }
    onActionComplete && onActionComplete();
  };

  const onBundlePlanPrint = type => {
    const sortedBundleList = getSortedBundleList(bundleList, "Bundle", { cutOrderList, bundleOrderList, layList, order });
    printBundlePlan({
      order,
      cutOrderList,
      bundleOrderList,
      layList,
      bundleList: sortedBundleList,
      tenant,
      flowInfoFields,
      orderFields,
      bundleExportFields,
      i18nMessages: messages,
      resetDigits,
      type,
      unit: allUnitList.find(e => e.unitId === selectedRow?.unitId) || {}
    });
  };

  const onBundlePlanDownload = (layIds = []) => {
    const filename = truncateBundlePlanFilename(
      `BUNDLE_PLAN_${order?.poRef}_${order?.styles}_${order?.colors}_${!isEmpty(order?.customerFlowRefs) ? order?.customerFlowRefs : ""}`
        ?.replaceAll(".", "-")
        .replaceAll("/", "-")
    );

    const layFields = ["layNo", "extLayNo", "cutNo", "prodType"];
    const cutOrderFields = ["fpo", "unitId", "part", "lotType", "lot", "lotValue"];
    const bundleOrderFields = ["bundleOrderNo", "flowRefs", "customerFlowRefs", "destinations", "deliveries", "delModes"];
    const bundleFields = [
      "style",
      "color",
      "bundleNo",
      "sizeGroup",
      "size",
      "ratio",
      "shade",
      "shrinkage",
      "qty",
      "start",
      "end",
      "rolls",
      "supplierRolls"
    ];
    const partFields = Array(20)
      .fill("")
      .map((_, idx) => `part${idx + 1}`);
    const allowedFields = [...layFields, ...cutOrderFields, ...bundleOrderFields, ...bundleFields, ...partFields];

    const columnList = bundleExportFields
      .filter(field => allowedFields.includes(field))
      .map((field, idx) => ({
        colNo: idx + 1,
        dataKey: field,
        title: lodashGet(messages, `resources.bundling.bundleFields.${field}`, field)
      }));

    const partList = listFromCsv(orderConfig?.bundleParts || "[]");
    const partMap = partList.reduce((acc, part, idx) => ({ ...acc, [`part${idx + 1}`]: part }), {});

    const sortedBundleList = getSortedBundleList(bundleList, "Bundle", { cutOrderList, bundleOrderList, layList, order });
    const finalBundleList = sortedBundleList.map(bundle => ({ ...bundle, ...partMap }));

    const rowList = finalBundleList
      .filter(e => isEmpty(layIds) || layIds?.includes(e.lay?.id))
      .map((row, idx) => ({
        rowNo: idx + 1,
        cellList: columnList.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] }))
      }));

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      params: { filename },
      data: [{ sheetNo: 0, sheetName: "Bundle Plan", autoSizeColumn: false, excelData: { columnList, rowList } }]
    };

    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          var fileName = header ? header.match(/filename="(.+)"/)[1] : `${filename}.xlsx`;

          let blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
        }
        // onActionComplete && onActionComplete(rows);
      }
    });
  };

  const onBundleTagPrint = () => {
    const sortedBundleList = getSortedBundleList(bundleList, "Bundle", { cutOrderList, bundleOrderList, layList, order });
    printBundleTag({
      order,
      cutOrderList,
      bundleOrderList,
      layList,
      bundleList: sortedBundleList,
      bundleTagSetting,
      resetDigits
    });
  };

  const onSerialPlanPrint = () => {
    const sortedBundleList = getSortedBundleList(bundleList, "Serial", { cutOrderList, bundleOrderList, layList, order });
    printSerialPlan({
      order,
      cutOrderList,
      bundleOrderList,
      layList: layRows,
      bundleList: sortedBundleList,
      tenant,
      flowInfoFields,
      orderFields,
      i18nMessages: messages,
      resetDigits,
      unit: allUnitList.find(e => e.unitId === selectedRow?.unitId) || {}
    });
  };

  const onPanelReplacementPlanPrint = () => {
    const sortedBundleList = getSortedBundleList(bundleList, "Serial", { cutOrderList, bundleOrderList, layList, order });
    printPanelReplacementPlan({
      order,
      cutOrderList,
      bundleOrderList,
      layList: layRows,
      bundleList: sortedBundleList,
      rollInfoList,
      tenant,
      flowInfoFields,
      orderFields,
      i18nMessages: messages,
      resetDigits,
      unit: allUnitList.find(e => e.unitId === selectedRow?.unitId) || {}
    });
  };

  const defaultBundleBreakAt = listFromCsv(product?.bundleBreakAt).filter(e => e !== "Ratio");
  const bundlePartList = !isEmpty(orderConfig?.bundleParts) ? JSON.parse(orderConfig?.bundleParts) : [];
  const settingInitialValues = {
    bundleSize: orderConfig?.bundleSize,
    bundleTolerance: orderConfig?.bundleTolerance,
    bundleBreakAt: orderConfig?.bundleBreakAt?.split(",") || defaultBundleBreakAt,
    ...productList.reduce(
      (acc, p) => ({
        ...acc,
        [`${p.id}-bundleParts`]: !isEmpty(bundlePartList.find(b => Number(b.productId) === p.id)?.parts)
          ? listFromCsv(bundlePartList.find(b => Number(b.productId) === p.id)?.parts)
          : listFromCsv(p?.bundleParts) || []
      }),
      {}
    )
  };

  const colsSetting = useDeepMemo(() => {
    return [
      { dataKey: "bundleSize", title: "Bundle Size" },
      { dataKey: "bundleTolerance", title: "Bundle Tolerance" },
      { dataKey: "bundleBreakAt", title: "Bundle Break At" },
      ...productList.map(p => ({ dataKey: `${p.id}-bundleParts`, title: `Bundle Parts - ${p.name}` }))
    ];
  }, [productList]);

  const rowsSetting = useDeepMemo(() => {
    if (isEmpty(colsSetting)) return [];
    if (isEmpty(orderConfig?.bundleSize)) return [];
    const bundlePartList = !isEmpty(orderConfig?.bundleParts) ? JSON.parse(orderConfig?.bundleParts) : [];
    return [
      {
        bundleSize: orderConfig?.bundleSize,
        bundleTolerance: orderConfig?.bundleTolerance,
        bundleBreakAt: orderConfig?.bundleBreakAt?.split(",") || [],
        ...productList.reduce((acc, p) => ({ ...acc, [`${p.id}-bundleParts`]: bundlePartList.find(b => Number(b.productId) === p.id)?.parts }), {})
      }
    ];
  }, [colsSetting, settingInitialValues, orderConfig]);

  const isAllClosed = cutOrderList?.every(e => e.state === OState.CLOSED);

  const printBundleSheetOptions = Object.values(BundleSheetType);
  return (
    <div>
      <PageHeader title="Numbering & Bundling" />

      <BundlingQtyDrawer
        open={drawerState?.open || false}
        order={order}
        selectedRow={selectedRow}
        bundleOrderParentKey={selectedKey}
        bundleOrder={drawerState.bundleOrder}
        bundleList={bundleList}
        flowInfoList={drawerState.flowInfoList}
        onDrawerClose={() => setDrawerState({ open: false })}
        onRefresh={() => setVersion(v => v + 1)}
      />

      <LayDialog open={dialogState?.open} rollFields={rollFields} lay={dialogState?.lay} onClose={() => setDialogState({ open: false, lay: {} })} />

      <div className={classes.content}>
        <MuiTable
          key={version}
          title={translate("resources.rollPlan.title.cut_order", { _: "Cut Order" })}
          toolbar={true}
          searchable={true}
          pageable={true}
          searchKeys={["style", "color", "flowRef", "customerFlowRef", "fpo"]}
          idKey="key"
          columns={cutOrderColumns}
          rows={cutOrderRows}
          expandedColor={["#BFDDF8", "#7AB9F0"]}
          onRowClick={onRowClick}
          rowStyle={({ row }) => ({ backgroundColor: selectedKey === row.key ? "#BFDDF8" : "none" })}
          i18nKey="resources.grouping.fields.flowInfo"
        />

        <Tabs style={{ marginTop: "1.5em" }} value={tabIdx} onChange={(e, idx) => setTabIdx(idx)}>
          <SubTab label={translate("resources.bundling.title.bundle_order", { _: "Bundle Order" })} />
          <SubTab label={translate("resources.bundling.title.readyToPlan", { _: "Ready to Plan" })} />
        </Tabs>

        {/* Flow Details */}
        {tabIdx === 1 && (
          <div style={{ marginTop: "1.5em" }}>
            <MuiTable
              key={version}
              title={<CardHeader style={{ padding: 0 }} title={"Flow Details"} />}
              toolbar={true}
              searchable={true}
              pageable={bundleOrderRows.length > 10}
              searchKeys={["style", "color", "flowRef", "customerFlowRef", "fpo"]}
              idKey="key"
              columns={bundleOrderColumns}
              rows={bundleOrderRows}
              selectAll={true}
              selectable={hasPrivilege(roles, hasAccess, ResourceId.NUMBERING_BUNDLING, Scope.WRITE)}
              selectActions={[]}
              onSelect={onSelect}
              i18nKey="resources.bulk.fields.flowInfo"
            />

            {hasPrivilege(roles, hasAccess, ResourceId.NUMBERING_BUNDLING, Scope.WRITE) && (
              <div className={classes.footer}>
                <Button
                  label="Create Bundle Order"
                  variant="contained"
                  color="primary"
                  disabled={busy || isEmpty(selectedKeys)}
                  onClick={onBundleOrderCreateClick}
                />
              </div>
            )}
          </div>
        )}

        {tabIdx === 0 && isEmpty(bundleOrderList) && (
          <Alert
            variant="filled"
            severity="warning"
            className={classes.mrTop}
            style={{
              backgroundColor: "#ffa726",
              color: "#ffffff"
            }}>
            <Typography variant="h6">{processingLayAction?.message}</Typography>
          </Alert>
        )}

        {tabIdx === 0 && !isEmpty(bundleOrderList) && (
          <div style={{ marginTop: "1.5em" }}>
            {/* Bundle Order */}
            <Card>
              <PerfectScrollbar>
                <Table
                  classes={{ table: classes.table }}
                  columns={bundleOrderColumns}
                  rows={bundleOrderRows}
                  setRows={setBundleOrderRows}
                  cellStyle={({ column }) => ({ width: column.dataKey === "state" ? 180 : undefined })}
                />
              </PerfectScrollbar>
              <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", padding: "0.5em 1.5em" }}>
                {!editingSequence && <Button label="Edit Sequence" variant="outlined" color="primary" onClick={() => setEditingSequence(true)} />}
                {editingSequence && <Button label="Cancel" variant="outlined" color="primary" onClick={() => setEditingSequence(false)} />}
                {editingSequence && (
                  <Button style={{ marginLeft: "1.5em" }} label="Save" variant="contained" color="primary" onClick={onSequenceUpdate} />
                )}
              </div>
            </Card>

            {/** Bundle Setting */}
            <div className={classes.mrTop}>
              <WithReduxForm initialValues={settingInitialValues}>
                {({ handleSubmit }) => (
                  <Card className={classes.mrTop}>
                    <CardHeader title="Bundle Setting" />
                    <Divider />
                    <div>
                      {editingSetting && (
                        <SimpleForm component="div" footer={false} className={classes.settings}>
                          <NumberInput label="Bundle Size" source="bundleSize" validate={[minValue(1)]} {...inputOptions(2)} />
                          <NumberInput
                            label="Bundle Tolerance"
                            source="bundleTolerance"
                            validate={[minValue(0), maxValue(10)]}
                            {...inputOptions(2)}
                          />
                          <SelectArrayInput
                            label="Bundle Break At"
                            source="bundleBreakAt"
                            choices={choicesBundleBreak}
                            allowEmpty
                            {...inputOptions(2)}
                          />
                          {productList.map(p => {
                            const bundleParts = listFromCsv(p?.bundleParts) || [];
                            let choicesParts = bundleParts.filter(e => !isEmpty(e)).map(e => ({ id: e, name: e }));
                            return (
                              <SelectArrayInput
                                key={p.id}
                                label={`Bundle Parts - ${p.name}`}
                                source={`${p.id}-bundleParts`}
                                choices={choicesParts}
                                allowEmpty
                                {...inputOptions(productList.length === 1 ? 6 : 3)}
                              />
                            );
                          })}
                        </SimpleForm>
                      )}
                      {!editingSetting && (
                        <div>
                          <MuiTable columns={colsSetting} rows={rowsSetting} />
                        </div>
                      )}
                    </div>

                    {isEmpty(bundleList) && !isAllClosed && hasPrivilege(roles, hasAccess, ResourceId.CUT_ORDER_SETTING, Scope.UPDATE) && (
                      <React.Fragment>
                        <Divider />
                        <div className={classes.footerSetting}>
                          {!editingSetting && <Button label="Edit" color="primary" variant="outlined" onClick={() => setEditingSetting(true)} />}
                          {editingSetting && (
                            <Button label="Cancel" color="primary" onClick={() => setEditingSetting(false)} style={{ marginRight: "1em" }} />
                          )}
                          {editingSetting && <Button label="Save" color="primary" variant="outlined" onClick={handleSubmit(onSettingUpdate)} />}
                        </div>
                      </React.Fragment>
                    )}
                  </Card>
                )}
              </WithReduxForm>
            </div>

            {/** Bundling Report */}
            <BundlingReport
              order={order}
              flowInfoList={selectedRow.flowInfoList}
              cutOrderList={cutOrderList}
              bundleOrderList={bundleOrderList}
              layRowList={layRowList}
              bundleList={bundleList}
            />

            {/** Lay List */}
            <div className={classes.mrTop}>
              <Collapse in={!isEmpty(processingLayAction)} timeout={350}>
                <Fade in={!isEmpty(processingLayAction)} timeout={350}>
                  <Alert
                    variant="filled"
                    severity="warning"
                    className={classes.mrTop}
                    style={{
                      backgroundColor: "#ffa726",
                      color: "#ffffff",
                      borderRadius: 6,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.16)"
                    }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <CircularProgress
                        size={18}
                        thickness={5}
                        style={{
                          color: "#ffffff",
                          marginRight: 12
                        }}
                      />

                      <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                        {processingLayAction?.message}
                      </Typography>
                    </div>
                  </Alert>
                </Fade>
              </Collapse>

              <MuiTable
                title={<CardHeader style={{ padding: 0 }} title={"Lay List"} />}
                toolbar
                selectAll
                selectable
                columns={layColumns}
                rows={layRows}
                sortable={true}
                inlineActions={layInlineActions}
                selectActions={laySelectActions}
                onInlineActionClick={onInlineActionClick}
                onSelectActionClick={onLaySelectActionClick}
                i18nKey="resources.layslip.fields.layslip"
              />
            </div>

            <div className={classes.footer} style={{ justifyContent: "flex-start" }}>
              <SplitButton
                primaryIcon={<ActionPrint />}
                primaryLabel="Bundle Plan"
                options={printBundleSheetOptions}
                style={{ marginRight: "1em" }}
                onClick={({ type }) => onBundlePlanPrint(type)}
              />

              <Button
                label="Bundle Sheet"
                variant="outlined"
                onClick={() => onBundlePlanDownload()}
                style={{ marginRight: "1em", color: "green", borderColor: "green" }}>
                <ExcelIcon style={{ marginRight: 4 }} />
              </Button>
              <Button label="Bundle Tag" color="primary" variant="outlined" onClick={onBundleTagPrint} style={{ marginRight: "1em" }}>
                <ActionPrint />
              </Button>
              <Button label="Serial Plan" color="primary" variant="outlined" onClick={onSerialPlanPrint} style={{ marginRight: "1em" }}>
                <ActionPrint />
              </Button>
              <Button label="Panel Replacement Plan" color="primary" variant="outlined" onClick={onPanelReplacementPlanPrint}>
                <ActionPrint />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default translate(BundlingView);
